'use client'

import * as React from 'react'
import { requestIdleCallbackPonyfill } from 'utils/dom'

const STUDIO_BASE_URL = process.env.NEXT_PUBLIC_STUDIO_CF_URL ?? ''

interface SeedanceVideoItemProps {
  src: string
  tryItLabel: string
  'data-card'?: true
}

export function SeedanceVideoItem({ src, tryItLabel, ...rest }: SeedanceVideoItemProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [isInView, setIsInView] = React.useState(false)

  React.useEffect(() => {
    const el = containerRef.current
    if (!el) {
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(true)
        } else {
          setIsInView(false)
        }
      },
      { threshold: 0, rootMargin: '50px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  React.useEffect(() => {
    const video = videoRef.current
    if (!video) {
      return
    }

    if (isInView) {
      requestIdleCallbackPonyfill(() => {
        video.preload = 'auto'
        void video.play().catch(() => {})
      })
      return
    }

    video.pause()
    video.currentTime = 0
  }, [isInView])

  return (
    <div ref={containerRef} className="shrink-0 w-[180px] lg:w-[210px] snap-center" {...rest}>
      <a
        href={`${STUDIO_BASE_URL}/ai-video-generator`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={tryItLabel}
        className="group relative block aspect-[3/4] w-full overflow-hidden rounded-sm border border-cf-sf-gray-350/40 bg-cf-grey-200 focus-visible:ring-2 focus-visible:ring-cf-green focus-visible:outline-none"
      >
        <video
          ref={videoRef}
          src={src}
          muted
          loop
          playsInline
          preload="none"
          className="size-full object-cover transition-transform duration-200 group-hover:scale-105"
          onError={e => e.stopPropagation()}
        />
      </a>
    </div>
  )
}
